import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaImages, FaGripVertical, FaSave } from 'react-icons/fa';
import SortableList from '../components/SortableList';

const turkishMap = {
  'ç': 'c', 'Ç': 'c', 'ş': 's', 'Ş': 's', 'ğ': 'g', 'Ğ': 'g',
  'ı': 'i', 'İ': 'i', 'ö': 'o', 'Ö': 'o', 'ü': 'u', 'Ü': 'u',
};

const generateSlug = (text) => {
  return text
    .split('')
    .map((char) => turkishMap[char] || char)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const emptyService = {
  title: '',
  slug: '',
  short_description: '',
  is_active: true,
  meta_title: '',
  meta_description: '',
  og_image: '',
};

export default function AdminServices() {
  useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyService });
  const [saving, setSaving] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [imageManagerService, setImageManagerService] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await api.get('/services/all');
      setServices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Hizmetler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyService });
    setShowModal(true);
  };

  const openEdit = (service) => {
    setEditing(service);
    setForm({
      title: service.title || '',
      slug: service.slug || '',
      short_description: service.short_description || '',
      is_active: service.is_active !== undefined ? service.is_active : true,
      meta_title: service.meta_title || '',
      meta_description: service.meta_description || '',
      og_image: service.og_image || '',
    });
    setShowModal(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'title' && !editing) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/services/${editing.id}`, form);
        toast.success('Hizmet güncellendi');
        setShowModal(false);
        fetchServices();
      } else {
        const res = await api.post('/services', form);
        toast.success('Hizmet eklendi');
        setShowModal(false);
        await fetchServices();
        // Yeni hizmet oluşturduktan sonra direkt görsel yöneticisine yönlendir
        setImageManagerService(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = (newServices) => {
    setServices(newServices);
    setOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    try {
      const ids = services.map((s) => s.id);
      await api.put('/services/reorder', { ids });
      toast.success('Siralama kaydedildi');
      setOrderChanged(false);
      fetchServices();
    } catch (err) {
      toast.error('Siralama kaydedilirken hata olustu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hizmeti ve tüm görsellerini silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success('Hizmet silindi');
      fetchServices();
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  const onImagesChanged = (updatedService) => {
    setServices((prev) => prev.map((s) => (s.id === updatedService.id ? updatedService : s)));
    setImageManagerService(updatedService);
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Hizmet Yönetimi</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {orderChanged && (
              <button className="btn btn-success" onClick={handleSaveOrder}>
                <FaSave /> Siralamayi Kaydet
              </button>
            )}
            <button className="btn btn-primary" onClick={openAdd}>
              <FaPlus /> Yeni Hizmet
            </button>
          </div>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Kapak</th>
              <th>Başlık</th>
              <th>Slug</th>
              <th>Görsel Sayısı</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {services.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  Henüz hizmet eklenmemiş
                </td>
              </tr>
            ) : (
              <SortableList
                items={services}
                onReorder={handleReorder}
                renderItem={({ item: service, ref, style, attributes, listeners }) => {
                  const cover = service.images?.[0]?.image_path;
                  return (
                    <tr key={service.id} ref={ref} style={style} className="sortable-row">
                      <td>
                        <span className="sortable-drag-handle" {...attributes} {...listeners}>
                          <FaGripVertical />
                        </span>
                      </td>
                      <td>
                        {cover ? (
                          <img src={getImageUrl(cover)} alt={service.title} />
                        ) : (
                          <span style={{ color: '#999', fontSize: 12 }}>Görsel yok</span>
                        )}
                      </td>
                      <td>{service.title}</td>
                      <td><code>{service.slug}</code></td>
                      <td>{service.images?.length || 0}</td>
                      <td>
                        <span className={`badge ${service.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {service.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td>
                        <div className="actions">
                          <button className="btn btn-secondary btn-sm" onClick={() => setImageManagerService(service)} title="Görselleri Yönet">
                            <FaImages />
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={() => openEdit(service)} title="Düzenle">
                            <FaEdit />
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(service.id)} title="Sil">
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }}
              />
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Hizmet Düzenle' : 'Yeni Hizmet'}</h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <label>Başlık</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Kısa Açıklama</label>
                <textarea
                  value={form.short_description}
                  onChange={(e) => handleChange('short_description', e.target.value)}
                  style={{ minHeight: '60px' }}
                />
              </div>
              <div className="form-group">
                <label>Durum</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                  <span>{form.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Meta Başlık</label>
                <input
                  type="text"
                  value={form.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Meta Açıklama</label>
                <input
                  type="text"
                  value={form.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                />
              </div>
              <div className="form-group full-width">
                <label>OG Image URL</label>
                <input
                  type="text"
                  value={form.og_image}
                  onChange={(e) => handleChange('og_image', e.target.value)}
                  placeholder="Sosyal medya paylasim gorseli URL'si"
                />
                {form.og_image && (
                  <img src={form.og_image} alt="OG Preview" className="image-preview" style={{ maxHeight: '100px', marginTop: '8px' }} />
                )}
              </div>
              {!editing && (
                <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
                  Hizmet oluşturulduktan sonra fotoğraflarını yükleyebileceksin.
                </p>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor...' : editing ? 'Kaydet' : 'Oluştur ve Fotoğraf Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {imageManagerService && (
        <ImageManagerModal
          service={imageManagerService}
          onClose={() => {
            setImageManagerService(null);
            fetchServices();
          }}
          onChange={onImagesChanged}
        />
      )}
    </div>
  );
}

function ImageManagerModal({ service, onClose, onChange }) {
  const [images, setImages] = useState(service.images || []);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setImages(service.images || []);
  }, [service.images, service.id]);

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(selected);
    setPreviews(selected.map((f) => URL.createObjectURL(f)));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error('En az bir fotoğraf seçin');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f));
      const res = await api.post(`/services/${service.id}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newImages = [...images, ...res.data];
      setImages(newImages);
      onChange({ ...service, images: newImages });
      toast.success(`${res.data.length} fotoğraf eklendi`);
      setFiles([]);
      setPreviews([]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Yükleme sırasında hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Bu fotoğrafı silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/services/${service.id}/images/${imageId}`);
      const newImages = images.filter((img) => img.id !== imageId);
      setImages(newImages);
      onChange({ ...service, images: newImages });
      toast.success('Fotoğraf silindi');
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const newImages = [...images];
    [newImages[index], newImages[target]] = [newImages[target], newImages[index]];
    setImages(newImages);
    try {
      await api.put(`/services/${service.id}/images/reorder`, {
        ids: newImages.map((img) => img.id),
      });
      onChange({ ...service, images: newImages });
    } catch (err) {
      toast.error('Sıralama güncellenemedi');
      setImages(images);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="admin-modal-header">
          <h2>{service.title} — Fotoğraflar</h2>
          <button className="admin-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleUpload} className="admin-form" style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Fotoğraf Ekle</h3>
          <div className="form-group">
            <label>Fotoğraflar (birden fazla seçebilirsiniz)</label>
            <input type="file" accept="image/*" multiple onChange={handleFilesChange} />
          </div>
          {previews.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {previews.map((src, i) => (
                <img key={i} src={src} alt="Önizleme" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4 }} />
              ))}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Yükleniyor...' : files.length > 0 ? `${files.length} Fotoğrafı Yükle` : 'Yükle'}
            </button>
          </div>
        </form>

        <h3 style={{ marginTop: 0 }}>Mevcut Fotoğraflar ({images.length})</h3>
        {images.length === 0 ? (
          <p style={{ color: '#888' }}>Henüz fotoğraf eklenmemiş.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }}>
            {images.map((img, idx) => (
              <div key={img.id} style={{ border: '1px solid #eee', borderRadius: 6, padding: '0.5rem' }}>
                <img src={getImageUrl(img.image_path)} alt="Hizmet görseli" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4, marginTop: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => move(idx, -1)} disabled={idx === 0} title="Geri">←</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => move(idx, 1)} disabled={idx === images.length - 1} title="İleri">→</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteImage(img.id)} title="Sil">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
